import { tns } from 'tiny-slider/src/tiny-slider';
import { debounce } from '@/utils/debounce';
import objectFitImages from 'object-fit-images';

class WPCarousel extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._slider = null;
    this._container = null;

    this._handleResize = debounce(() => {
      if (this._slider && this.matchHeight) {
        this._setSlideItemsHeight();
      }
    }, 50);
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        try {
          this._setupSlider();
          this.init();
        } catch (error) {
          console.warn(error);
        }
      },
      { once: true },
    );
    return self;
  }

  static get is() {
    return 'wp-carousel';
  }

  /**
   * Getter for `draggable` attribute
   */
  get draggable() {
    return this.hasAttribute('draggable');
  }

  /**
   * Setter for `draggable` attribute
   */
  set draggable(value) {
    if (Boolean(value)) {
      this.setAttribute('draggable', '');
    } else {
      this.removeAttribute('draggable');
    }
  }

  /**
   * Getter for `items` attribute
   */
  get items() {
    return this.hasAttribute('items') ? Number(this.getAttribute('items')) : 1;
  }

  /**
   * Setter for `items` attribute
   */
  set items(value) {
    if (Number(value)) {
      this.setAttribute('items', Number(value));
    } else {
      this.removeAttribute('items');
    }
  }

  /**
   * Getter for `speed` attribute
   */
  get speed() {
    return this.hasAttribute('speed')
      ? parseInt(this.getAttribute('speed'), 10)
      : 300;
  }

  /**
   * Getter for `disable` attribute
   */
  get disable() {
    return this.hasAttribute('disable');
  }

  /**
   * Setter for `disable` attribute
   */
  set disable(value) {
    if (Boolean(value)) {
      this.setAttribute('disable', '');
    } else {
      this.removeAttribute('disable');
    }
  }

  /**
   * Getter for `nav` attribute
   */
  get nav() {
    if (this.hasAttribute('nav') && this.getAttribute('nav') === 'false') {
      return false;
    }

    return true;
  }

  /**
   * Getter for `controls` attribute
   */
  get controls() {
    if (
      this.hasAttribute('controls') &&
      this.getAttribute('controls') === 'false'
    ) {
      return false;
    }

    return true;
  }

  /**
   * Getter for `navContainer` attribute
   */
  get navContainer() {
    return this.hasAttribute('nav-container')
      ? document.querySelector(this.getAttribute('nav-container'))
      : false;
  }

  /**
   * Getter for `controlsContainer` attribute
   */
  get controlsContainer() {
    return this.hasAttribute('controls-container')
      ? document.querySelector(this.getAttribute('controls-container'))
      : false;
  }

  /**
   * Getter for `animateIn` attribute
   */
  get animateIn() {
    return this.hasAttribute('animate-in')
      ? this.getAttribute('animate-in')
      : 'tns-fadeIn';
  }

  /**
   * Getter for `animateOut` attribute
   */
  get animateOut() {
    return this.hasAttribute('animate-out')
      ? this.getAttribute('animate-out')
      : 'tns-fadeOut';
  }

  /**
   * Getter for `slide-by` attribute
   */
  get slideBy() {
    return this.hasAttribute('slide-by')
      ? Number(this.getAttribute('slide-by'))
      : 'page';
  }

  /**
   * Setter for `slide-by` attribute
   */
  set slideBy(value) {
    if (Number(value)) {
      this.setAttribute('slide-by', Number(value));
    } else {
      this.removeAttribute('slide-by');
    }
  }

  /**
   * Getter for `gutter` attribute
   */
  get gutter() {
    return this.hasAttribute('gutter')
      ? parseInt(this.getAttribute('gutter'))
      : 0;
  }

  /**
   * Setter for `gutter` attribute
   */
  set gutter(value) {
    if (Number(value)) {
      this.setAttribute('gutter', parseInt(value));
    } else {
      this.removeAttribute('gutter');
    }
  }

  /**
   * Getter for `width` attribute
   */
  get width() {
    return this.hasAttribute('width')
      ? parseInt(this.getAttribute('width'))
      : false;
  }

  /**
   * Setter for `width` attribute
   */
  set width(value) {
    if (Number(value)) {
      this.setAttribute('width', parseInt(value));
    } else {
      this.removeAttribute('width');
    }
  }

  /**
   * Getter for `breakpoint` attribute
   */
  get breakpoint() {
    return this.hasAttribute('breakpoint')
      ? parseInt(this.getAttribute('breakpoint'))
      : 768;
  }

  /**
   * Setter for `breakpoint` attribute
   */
  set breakpoint(value) {
    if (Number(value)) {
      this.setAttribute('breakpoint', parseInt(value));
    } else {
      this.removeAttribute('breakpoint');
    }
  }

  /**
   * Getter for `lazyload` attribute
   */
  get lazyload() {
    return this.hasAttribute('lazyload');
  }

  /**
   * Setter for `lazyload` attribute
   */
  set lazyload(value) {
    if (Boolean(value)) {
      this.setAttribute('lazyload', '');
    } else {
      this.removeAttribute('lazyload');
    }
  }

  /**
   * Getter for `match-height` attribute
   */
  get matchHeight() {
    return this.hasAttribute('match-height');
  }

  /**
   * Setter for `match-height` attribute
   */
  set matchHeight(value) {
    if (Boolean(value)) {
      this.setAttribute('match-height', '');
    } else {
      this.removeAttribute('match-height');
    }
  }

  /**
   * Getter for `auto-height` attribute
   */
  get autoHeight() {
    return this.hasAttribute('auto-height');
  }

  /**
   * Setter for `auto-height` attribute
   */
  set autoHeight(value) {
    if (Boolean(value)) {
      this.setAttribute('auto-height', '');
    } else {
      this.removeAttribute('auto-height');
    }
  }

  /**
   * Getter for `autoplay` attribute
   */
  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  /**
   * Setter for `autoplay` attribute
   */
  set autoplay(value) {
    if (Boolean(value)) {
      this.setAttribute('autoplay', '');
    } else {
      this.removeAttribute('autoplay');
    }
  }

  /**
   * Getter for `loop` attribute
   */
  get loop() {
    return this.hasAttribute('loop');
  }

  /**
   * Setter for `loop` attribute
   */
  set loop(value) {
    if (Boolean(value)) {
      this.setAttribute('loop', '');
    } else {
      this.removeAttribute('loop');
    }
  }

  /**
   * Getter for `gallery` attribute
   */
  get gallery() {
    return this.hasAttribute('gallery');
  }

  /**
   * Setter for `gallery` attribute
   */
  set gallery(value) {
    if (Boolean(value)) {
      this.setAttribute('gallery', '');
    } else {
      this.removeAttribute('gallery');
    }
  }

  /**
   * Getter for `prefix` attribute
   */
  get prefix() {
    return this.hasAttribute('prefix')
      ? this.getAttribute('prefix')
      : 'c-carousel';
  }

  /**
   * Setter for `prefix` attribute
   */
  set prefix(value) {
    if (String(value.length)) {
      this.setAttribute('prefix', value);
    } else {
      this.removeAttribute('prefix');
    }
  }

  get _breakpoint() {
    return this.width ? this.width + this.gutter * 2 : this.breakpoint;
  }

  connectedCallback() {
    // If slider already setup, return
    if (this._slider) {
      this._slider.refresh();
      this._handleResize();
      if (!this.loop) {
        this._checkItemsInView();
      }
      return;
    }
  }

  disconnectedCallback() {
    if (this.matchHeight) {
      window.removeEventListener('resize', this._handleResize, false);
    }

    this.destroy();
  }

  destroy() {
    if (this._slider && typeof this._slider.destroy === 'function') {
      this._slider.destroy();
    }
  }

  reset() {
    if (this.querySelector('.tns-inner')) {
      this.querySelector('.tns-inner').style.height = null;
    }

    if (this._slider && this.matchHeight) {
      this._setSlideItemsHeight();
    }
  }

  _setupSlider() {
    // Add class
    this.classList.add(this.prefix);

    if (this.gallery) {
      this.classList.add(`${this.prefix}--gallery`);
    }

    // Add classes to child elements
    [...this.children].forEach((child) => {
      child.classList.add(`${this.prefix}__item`);
    });

    // Create container element
    this._container = document.createElement('div');
    this._container.className = `${this.prefix}__carousel`;

    // Shove innerHTML intro div
    [...this.children].forEach((child) => {
      this._container.appendChild(child);
    });

    // Append
    this.appendChild(this._container);
  }

  _handleTransitionEnd(info) {
    // If info event not container, return
    if (info.event.target !== info.container) {
      return;
    }

    if (!this.loop) {
      this._checkItemsInView();
    }
  }

  _checkItemsInView() {
    const overflow = this._isCarouselOverflowing;

    if (overflow.left) {
      this.classList.add('left-is-overflow');
    } else {
      this.classList.remove('left-is-overflow');
    }

    if (overflow.right) {
      this.classList.add('right-is-overflow');
    } else {
      this.classList.remove('right-is-overflow');
    }
  }

  get _isCarouselOverflowing() {
    const rect = this._container.getBoundingClientRect();
    const left = rect.left < 0;
    const right =
      rect.right > (window.innerWidth || document.documentElement.clientWidth);

    return {
      left,
      right,
    };
  }

  _setSlideItemsHeight() {
    const info = this._slider.getInfo();
    let largest = 0;

    [...info.slideItems].forEach((item) => {
      // Reset height
      item.style.height = '';

      if (item.scrollHeight > largest) {
        largest = item.offsetHeight;
      }
    });

    // If less than breakpoint, return
    if (window.innerWidth < this.breakpoint && this.autoHeight) {
      return;
    }

    // Set items to largest height
    [...info.slideItems].forEach((item) => {
      item.style.height = `${largest}px`;
    });
  }

  get _responsiveConfig() {
    const obj = {};

    obj[this._breakpoint] = {
      items: Math.round(this.items / 2),
      fixedWidth: this.width,
      controls: false,
      gutter: this.gutter,
    };

    obj[(this._breakpoint + 1200) / 2] = {
      items: Math.round(this.items * 0.75),
    };

    obj['768'] = {
      controls: true,
    };

    obj['1200'] = {
      items: this.items,
    };

    return obj;
  }

  init() {
    if (!this._container) {
      return;
    }

    // Setup slider
    this._slider = tns({
      speed: this.speed,
      disable: this.disable,
      nav: this.nav,
      items: this.items,
      controls: this.controls,
      navContainer: this.navContainer,
      controlsContainer: this.controlsContainer,
      animateIn: this.animateIn,
      animateOut: this.animateOut,
      container: this._container,
      slideBy: this.slideBy,
      autoplay: this.autoplay,
      mouseDrag: this.draggable,
      loop: this.loop,
      lazyload: this.lazyload,
      navAsThumbnails: true,
      controlsText: [
        '<span class="u-visuallyhidden">Previous</span>',
        '<span class="u-visuallyhidden">Next</span>',
      ],
      fixedWidth: false,
      gutter: this.gutter,
      // responsive: this._responsiveConfig,
      autoHeight: this.autoHeight,
      mode: this.gallery ? 'gallery' : 'carousel',
    });

    // Remove slider attribute from lazyload images
    setTimeout(() => {
      if (!this.loop) {
        this._checkItemsInView();
      }
      this.$emit('sliderinit');

      if (this.matchHeight) {
        this._setSlideItemsHeight();
        this._handleResize = this._handleResize.bind(this);
        window.addEventListener('resize', this._handleResize, false);
      }

      // Reset images
      objectFitImages();
    });

    // Add event listeners
    this._slider.events.on(
      'transitionEnd',
      this._handleTransitionEnd.bind(this),
    );
  }
}

customElements.define(WPCarousel.is, WPCarousel);
